const DEFAULT_BRAND_COLOR = '#6366f1';

export function normalizeBrandColor(
  value: string | undefined,
  fallback = DEFAULT_BRAND_COLOR,
): string {
  const candidate = (value || '').trim();
  if (/^#[0-9a-f]{6}$/i.test(candidate)) return candidate;
  if (/^#[0-9a-f]{3}$/i.test(candidate)) {
    return `#${candidate
      .slice(1)
      .split('')
      .map((part) => `${part}${part}`)
      .join('')}`;
  }
  return fallback;
}

export function brandColorRgb(value: string) {
  const normalized = normalizeBrandColor(value);
  return {
    r: Number.parseInt(normalized.slice(1, 3), 16),
    g: Number.parseInt(normalized.slice(3, 5), 16),
    b: Number.parseInt(normalized.slice(5, 7), 16),
  };
}

export function darkenBrandColor(value: string, amount: number): string {
  const { r, g, b } = brandColorRgb(value);
  const channel = (part: number) =>
    Math.max(0, Math.round(part * (1 - amount)))
      .toString(16)
      .padStart(2, '0');
  return `#${channel(r)}${channel(g)}${channel(b)}`;
}

function relativeLuminance(value: string): number {
  const { r, g, b } = brandColorRgb(value);
  const channels = [r, g, b].map((part) => {
    const normalized = part / 255;
    return normalized <= 0.03928
      ? normalized / 12.92
      : ((normalized + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * channels[0] + 0.7152 * channels[1] + 0.0722 * channels[2];
}

function contrastRatio(first: string, second: string): number {
  const lighter = Math.max(relativeLuminance(first), relativeLuminance(second));
  const darker = Math.min(relativeLuminance(first), relativeLuminance(second));
  return (lighter + 0.05) / (darker + 0.05);
}

export function readableBrandForeground(value: string): '#111827' | '#ffffff' {
  return contrastRatio(value, '#111827') >= contrastRatio(value, '#ffffff')
    ? '#111827'
    : '#ffffff';
}

/** A brand-derived text color that remains readable on a white surface. */
export function readableBrandInk(value: string): string {
  const normalized = normalizeBrandColor(value);
  for (let amount = 0; amount <= 0.85; amount += 0.05) {
    const candidate = darkenBrandColor(normalized, amount);
    if (contrastRatio(candidate, '#ffffff') >= 4.5) return candidate;
  }
  return '#111827';
}
